import { IonButton, IonList, IonSpinner, IonToast } from "@ionic/react";
import { useFormik } from "formik";
import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import * as Yup from "yup";
import ChannelPartnerFields from "./ChannelPartnerFields";
import ChannelPartnerMediaUploads from "./ChannelPartnerMediaUploads";
import DealerOwnerFields from "./DealerOwnerFields";
import { handleErrors, Regex } from "../services/common.service";
import { uploadFile } from "../services/image.service";
import {
  InventoryLocationPartnerType,
  patchInventoryLocation,
  postInventoryLocation,
  uploadDocument,
} from "../services/inventoryLocation.service";
import { registerUser, saveRole } from "../services/user.service";

// Exported so it can be unit-tested directly (see
// CreateInventoryLocation.test.ts) without rendering the whole Ionic form:
// parentId (the distributor picker) is required only for a Retailer.
// billing/shipping address and the captured GPS point are required for
// both partner types. ownerEmail is required only when creating a
// brand-new dealer account (not when reusing an existing one -- the
// dealer search doesn't return email).
export function buildInventoryLocationValidationSchema(isDistributor: boolean) {
  return Yup.object({
    businessName: Yup.string().trim().required("Required"),
    parentId: Yup.string().when([], {
      is: () => !isDistributor,
      then: Yup.string().required("Select the parent distributor"),
    }),
    socialHandle: Yup.string().trim().matches(Regex.socialHandle, {
      message: "Letters, numbers, periods and underscores only -- no spaces, and can't start/end with or repeat a period",
      excludeEmptyString: true,
    }),
    billingAddress: Yup.string().trim().required("Required"),
    shippingAddress: Yup.string().trim().required("Required"),
    geoLocation: Yup.mixed().required("Tap the location icon to capture the shop's GPS location"),
    ownerName: Yup.string().trim().required("Required"),
    ownerMobile: Yup.string().trim().matches(Regex.mobile, "Invalid mobile number").required("Required"),
    ownerEmail: Yup.string().when("existingOwnerSelected", {
      is: (v: boolean) => !v,
      then: Yup.string().trim().email("Invalid email address").required("Required"),
    }),
  });
}

// Shared form for both "Add Distributor" and "Add Retailer" quick links --
// they differ only in the distributor picker (Retailer only) and whether
// billing/shipping address is required (Distributor only). Retailers link
// to their parent distributor via the existing InventoryLocation.parentId
// field. businessName doubles as the InventoryLocation's own `name` -- no
// separate "shop name" question, since it was the same value in practice.
//
// Shop-level fields (ChannelPartnerFields) and photo/GST uploads
// (ChannelPartnerMediaUploads) are shared with the edit flow
// (EditInventoryLocation.tsx). Photo/GST are shown inline in `deferred`
// mode here -- the underlying upload endpoints are keyed by location id in
// the URL, so the actual upload can't happen until the location exists,
// but the salesperson picks the files up front like every other field
// instead of a separate "attach media" screen after Create. The picked
// files are uploaded right after the location is created, as part of the
// same submit.
const CreateInventoryLocation: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const partnerType: InventoryLocationPartnerType =
    (location.state as any)?.partnerType ?? InventoryLocationPartnerType.RETAILER;
  const isDistributor = partnerType === InventoryLocationPartnerType.DISTRIBUTOR;

  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [gstFile, setGstFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [onSuccess, setOnSuccess] = useState("");
  const [onError, setOnError] = useState("");
  // Everything below the dealer-mobile-number lookup in DealerOwnerFields
  // (business/shop fields, media uploads) stays locked until that lookup
  // resolves -- see DealerOwnerFields' onResolvedChange for why.
  const [dealerResolved, setDealerResolved] = useState(false);

  const formik = useFormik({
    initialValues: {
      businessName: "",
      whatsappNo: "",
      email: "",
      gstNo: "",
      socialHandle: "",
      parentId: "",
      parentName: "",
      billingAddress: "",
      shippingAddress: "",
      village: "",
      subDistrict: "",
      district: "",
      state: "",
      country: "",
      geoLocation: undefined as any,
      existingOwnerSelected: false,
      ownerName: "",
      ownerFarmUserId: "",
      ownerCountryCode: "+91",
      ownerMobile: "",
      ownerEmail: "",
      ownerPassword: "",
      ownerGender: "Male",
    },
    validationSchema: buildInventoryLocationValidationSchema(isDistributor),
    onSubmit: async (values) => {
      setSaving(true);
      try {
        let ownerFarmUserId = values.ownerFarmUserId;
        if (!values.existingOwnerSelected) {
          const ownerPayload: any = {
            name: values.ownerName,
            farmUserId: values.ownerFarmUserId,
            countryCode: values.ownerCountryCode,
            mobile: values.ownerMobile,
            email: values.ownerEmail,
            gender: values.ownerGender,
            lang: "en",
          };
          if (values.ownerPassword) {
            ownerPayload.password = values.ownerPassword;
          }
          const owner = await registerUser(ownerPayload).then(handleErrors);
          if (!owner?.farmUserId) {
            throw new Error("Failed to create dealer account");
          }
          ownerFarmUserId = owner.farmUserId;
        }
        // Idempotent either way (PUT) -- safe to call even if an existing
        // dealer already has the role.
        await saveRole(ownerFarmUserId, "dealer").then(handleErrors);

        const locationPayload: any = {
          name: values.businessName,
          partnerType,
          businessName: values.businessName,
          whatsappNo: values.whatsappNo || undefined,
          email: values.email || undefined,
          gstNo: values.gstNo || undefined,
          // Business asked for "Insta or Facebook username" -- one field in
          // the UI, stored under both so either lookup on the model works.
          instagramHandle: values.socialHandle || undefined,
          facebookHandle: values.socialHandle || undefined,
          contact_no: values.ownerMobile ? [values.ownerMobile] : undefined,
          village: values.village || undefined,
          subDistrict: values.subDistrict || undefined,
          district: values.district || undefined,
          state: values.state || undefined,
          country: values.country || undefined,
          geoLocation: values.geoLocation,
          owner: [ownerFarmUserId],
          isChannelPartner: true,
          isProductionPartner: false,
          billingAddress: values.billingAddress,
          shippingAddress: values.shippingAddress,
        };
        if (!isDistributor) {
          locationPayload.parentId = values.parentId;
        }

        const createdLocation = await postInventoryLocation(locationPayload);

        let message = `${isDistributor ? "Distributor" : "Retailer"} created`;
        try {
          if (photoFile) {
            const uploadRes = await uploadFile(createdLocation.id, "shopFront", photoFile).then((res) => res.json());
            const photoUrl = uploadRes?.images?.[0];
            if (photoUrl) await patchInventoryLocation(createdLocation.id, { shopFrontPhotoUrl: photoUrl });
          }
          if (gstFile) {
            const docRes = await uploadDocument(gstFile);
            const gstUrl = docRes?.url?.[0];
            if (gstUrl) await patchInventoryLocation(createdLocation.id, { gstCertificateUrl: gstUrl });
          }
        } catch (mediaErr: any) {
          message += `. Photo/certificate upload failed (${mediaErr?.message ?? String(mediaErr)}) -- you can add it from Edit.`;
        }

        setOnSuccess(message);
        // history.goBack(), not navigateTo("/tabs/home") -- Home is
        // literally where this flow started (the only entry point is its
        // Quick Link buttons), so pushing another "/tabs/home" entry on top
        // just adds a duplicate of a route already in the stack: the URL
        // updates but IonRouterOutlet doesn't swap the visible page. Going
        // back pops cleanly to the existing Home instance. Delayed slightly
        // so the success toast is visible before the page changes.
        setTimeout(() => history.goBack(), 1200);
      } catch (err: any) {
        setOnError(err?.message ?? String(err));
      } finally {
        setSaving(false);
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <IonList>
        <DealerOwnerFields formik={formik} onResolvedChange={setDealerResolved} />
        <ChannelPartnerFields formik={formik} isDistributor={isDistributor} disabled={!dealerResolved} />
        <ChannelPartnerMediaUploads
          deferred
          disabled={!dealerResolved}
          onPhotoSelected={setPhotoFile}
          onGstSelected={setGstFile}
          onSuccess={setOnSuccess}
          onError={setOnError}
        />
      </IonList>

      <IonButton expand="block" type="submit" disabled={saving || !dealerResolved}>
        {saving ? <IonSpinner name="bubbles" /> : `Create ${isDistributor ? "Distributor" : "Retailer"}`}
      </IonButton>

      <IonToast isOpen={!!onSuccess} onDidDismiss={() => setOnSuccess("")} message={onSuccess} duration={2000} color="success" />
      <IonToast isOpen={!!onError} onDidDismiss={() => setOnError("")} message={onError} duration={2500} color="danger" />
    </form>
  );
};

export default CreateInventoryLocation;
